import { ReactNode, useEffect } from "react";
import { Close } from "../../../assets/icons";

interface ModalProps {
    title: string;
    width?: string;
    visible: boolean;
    children: ReactNode;
    onClose: () => void;
    showButtons?: boolean;
    yesButton?: string;
    noButton?: string;
    disabled?: boolean;
    onConfirm?: () => void;
}

const Modal = ({
    width,
    title,
    visible,
    onClose,
    onConfirm,
    children,
    yesButton,
    noButton,
    disabled,
    showButtons,
}: ModalProps) => {
    useEffect(() => {
        const handleEsc = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };

        if (visible) document.addEventListener("keydown", handleEsc, false);
        else document.removeEventListener("keydown", handleEsc, false);

        return () => {
            document.removeEventListener("keydown", handleEsc, false);
        };
    }, [onClose, visible]);

    const handleClickOutside = (event: React.MouseEvent<HTMLDivElement>) => {
        if ((event.target as HTMLElement).classList.contains("modal-overlay"))
            onClose();
    };

    if (!visible) return null;

    return (
        <div
            className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-75 modal-overlay z-[10011]"
            onClick={handleClickOutside}
        >
            <div
                className={`bg-darkcolor-700 rounded-[8px] border-1 border-darkcolor-400 shadow-md relative`}
                style={{ width, position: "relative", bottom: 100 }}
            >
                <div className="flex justify-between items-center px-[24px] py-[15px]">
                    <label className="text-[1.1875em] font-semibold leading-[23px] text-neutral-25">
                        {title}
                    </label>
                    <Close className="cursor-pointer" onClick={onClose} />
                </div>
                <hr className="w-full h-[1px] mx-auto my-0 bg-darkcolor-300 border-0 rounded md:my-0 dark:bg-darkcolor-300" />
                <div
                    className="justify-between w-full items-center px-[24px] py-[15px]"
                    onClick={(e) => e.stopPropagation()}
                >
                    {children}
                </div>
                {showButtons && (
                    <>
                        <hr className="w-full h-[1px] mx-auto my-0 bg-darkcolor-300 border-0 rounded md:my-0 dark:bg-darkcolor-300" />
                        <div className="flex justify-end items-center gap-[16px] p-[16px]">
                            <button
                                className="flex items-center justify-center py-2 px-4 gap-2 rounded-[8px] border-[1px] border-darkcolor-400 text-sm font-normal text-neutral-25 bg-darkcolor-600 hover:bg-darkcolor-500 hover:border-darkcolor-300 transition"
                                onClick={onClose}
                            >
                                {noButton}
                            </button>
                            <button
                                className={`flex items-center justify-center py-2 px-4 gap-2 rounded-[8px] border-[1px] border-primary-700 bg-primary-600 text-sm font-bold text-neutral-25 hover:bg-primary-500 hover:border-primary-600 transition ${disabled
                                        ? "opacity-50 cursor-not-allowed"
                                        : "bg-primary-600"
                                    }`}
                                onClick={onConfirm}
                            >
                                {yesButton}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Modal;
